<script setup lang="ts">
import { ref, defineOptions, onMounted, defineEmits } from "vue";
import { signIn, confirmSignIn } from "aws-amplify/auth";
import { getCookie, deleteCookie, setCookie } from "../utils/cookies";
import { useGeneralStore } from "../stores/general";
import GenericButton from "./ui/GenericButton.vue";
import { scrollToInput } from "../utils/common";

defineOptions({ name: "LoginPage" });
const general = useGeneralStore();
const emits = defineEmits(["change-view"]);

const loading = ref(false);
const showPassword = ref(false);
const username = ref("");
const password = ref("");
const errorMessage = ref("");

const sigInEmailAndPassword = async () => {
  loading.value = true;
  errorMessage.value = "";
  try {
    const signInParams = { username: username.value, password: password.value };
    const { isSignedIn, nextStep } = await signIn(signInParams);
    if (
      !isSignedIn &&
      nextStep.signInStep == "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
    ) {
      await confirmSignIn({ challengeResponse: password.value });
    }
    await general.generateFingerprint(() => {
      hasValidKey();
    });
  } catch (error: any) {
    console.error(error);
    errorMessage.value = "Correo o contraseña incorrectos";
    loading.value = false;
  }
};

function hasValidKey() {
  const currentKey = getCookie("booth");
  if (!currentKey) {
    emits("change-view", "setBoothKey");
  } else {
    const booth = getCookie("booth") as string;
    const days = general.getBooth.customConfig?.duracionSesion;
    setCookie("booth", booth, days);
    const fingerprint = getCookie("fingerprint") as string;
    general.getSession().then((session: any) => {
      const accessToken = session.tokens?.accessToken as object;
      if (!booth) {
        emits("change-view", "setBoothKey");
        return;
      }
      if (!fingerprint) {
        general.generateFingerprint(() => {
          hasValidKey();
        });
        return;
      }
      setCookie("fingerprint", fingerprint, days);
      general
        .postMedicalBoothsValidation({
          claveCabina: booth,
          fingerprint: fingerprint,
          accessToken: accessToken,
        })
        .then((res: any) => {
          if (res.code === 200) {
            general.setBooth(res.data);
            emits("change-view", "baseInformation");
          } else {
            const { idSesion } = res.message;
            if (idSesion) {
              general.deleteBoothSession(idSesion);
            }
            deleteCookie("booth");
            deleteCookie("fingerprint");
            emits("change-view", "setBoothKey");
          }
        });
    });
  }
  loading.value = false;
}

async function handleClick() {
  if (!document || !document.exitFullscreen) {
    errorMessage.value = "No se puede salir de pantalla completa...";
    return;
  }
  await document.exitFullscreen();
}

onMounted(() => {
  loading.value = true;
  general
    .getSession()
    .then((res) => (res.userSub ? hasValidKey() : (loading.value = false)));
});
</script>

<template>
  <div class="min-h-screen flex items-center justify-center px-4">
    <GenericButton
      variant="ghost"
      @click="handleClick"
      class="absolute top-0 right-3 text-primary-500"
    >
      Salir
    </GenericButton>
    <div
      class="flex flex-col items-center gap-4 rounded-lg shadow-lg border border-gray-300 p-4 text-center w-full"
    >
      <h1 class="text-2xl font-bold text-primary-900">Iniciar Sesión</h1>
      <hr class="my-4 border-gray-200 w-full" />
      <form class="flex flex-col gap-4" @submit.prevent="sigInEmailAndPassword">
        <div class="text-left">
          <label for="email" class="block text-sm font-medium text-primary-900"
            >Correo Electrónico</label
          >
          <input
            @focus="scrollToInput"
            type="email"
            id="email"
            name="email"
            class="w-full border border-gray-300 rounded p-2"
            placeholder="correo@ejemplo.com"
            required
            v-model="username"
          />
        </div>
        <div class="text-left">
          <label
            for="password"
            class="block text-sm font-medium text-primary-900"
            >Contraseña</label
          >
          <div class="flex items-center gap-1">
            <input
              @focus="scrollToInput"
              :type="showPassword ? 'text' : 'password'"
              id="password"
              name="password"
              class="w-full border border-gray-300 rounded p-2"
              placeholder="********"
              required
              v-model="password"
            />
            <button
              type="button"
              @click="showPassword = !showPassword"
              class="text-primary-900 cursor-pointer"
            >
              {{ showPassword ? "Ocultar" : "Mostrar" }}
            </button>
          </div>
        </div>
        <GenericButton
          variant="primary"
          type="submit"
          :disabled="loading"
          :loading="loading"
          class="bg-gradient-to-r from-primary-400 to-primary-900 text-white p-4 rounded-full mt-4"
        >
          Iniciar Sesión
        </GenericButton>
      </form>
      <p v-if="errorMessage" class="text-red-500 text-sm mt-2">
        {{ errorMessage }}
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Prevenir que algunos navegadores bloqueen el scroll */
html,
body {
  height: 100%;
  overscroll-behavior: contain;
}
</style>
